import { useApolloClient } from "@apollo/client";
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { InputWithValidationControl } from "src/components/Inputs/InputWithValidationControl";
import { TextareaWithValidationControl } from "src/components/Inputs/TextareaWithValidationControl";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { useCreateFormTemplate } from "src/generated/hooks/form-service/queries";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import { useEnrollmentPeriodContext } from "src/scenes/orgAdmin/enrollmentPeriods/Edit";
import { evictFormTemplateCache } from "src/services/apolloCacheManager";
import { validateWithZod } from "src/services/formValidations";
import {
  FORM_TEMPLATE_TAB_HASH,
  FormTemplates,
} from "src/services/url/OrgAdmin";
import * as GQL from "src/types/graphql";
import { z } from "zod";
import { generateKey } from "../ContentTab/V2/autoGenerateKey";
import { CREATE_FORM_TEMPLATE } from "../graphql/mutations";

const FormSchema = z.object({
  enrollment_period_id: z.string(),
  name: z.string().min(1, "Form name is required"),
  key: z.string().min(1, "Form key is required"),
  userGeneratedKey: z.boolean(),
  description: z.string().optional(),
});

type FormType = z.infer<typeof FormSchema>;

type Props = {
  isOpen: boolean;
  onCancel: () => void;
};

export const CreateFromScratchDialog: React.FC<Props> = ({
  isOpen,
  onCancel,
}) => {
  const navigate = useNavigate();
  const toast = useAvelaToast();
  const organization = useOrganization();
  const { enrollmentPeriod } = useEnrollmentPeriodContext();

  const flags = useFlags(["form-builder-draft-mode"]);

  const { mutateAsync: createFormTemplateDraft } = useCreateFormTemplate();
  const initialValues: FormType = {
    enrollment_period_id: enrollmentPeriod.id,
    name: "",
    key: "",
    description: "",
    userGeneratedKey: false,
  };

  const [createFormTemplatePublished] = useRemoteDataMutation<
    GQL.CreateFormTemplate,
    GQL.CreateFormTemplateVariables
  >(CREATE_FORM_TEMPLATE);

  const apolloClient = useApolloClient();

  const handleSubmitForm = useCallback(
    async (values: FormType) => {
      try {
        if (!organization.hasData()) {
          throw new Error("Missing organization");
        }

        if (flags["form-builder-draft-mode"].enabled) {
          const response = await createFormTemplateDraft({
            body: {
              enrollmentPeriodId: values.enrollment_period_id,
              name: values.name,
              key: values.key,
              description: values.description,
            },
          });

          if (!response.data?.data.id) {
            throw new Error("Missing form template id");
          }

          evictFormTemplateCache(apolloClient.cache);

          toast({
            title: "Form created",
            status: "success",
            isClosable: true,
          });
          navigate(
            FormTemplates.edit(
              organization.data,
              enrollmentPeriod.id,
              response.data.data.id,
              FORM_TEMPLATE_TAB_HASH.CONTENT
            )
          );
        } else {
          const variables: GQL.CreateFormTemplateVariables = {
            form_template: {
              enrollment_period_id: values.enrollment_period_id,
              name: values.name,
              key: values.key,
              description: values.description,
              show_waitlist_position: false,
            },
          };

          await createFormTemplatePublished({
            variables,
            update: evictFormTemplateCache,
            onCompleted: (data) => {
              const formTemplateId = data.insert_form_template_one?.id || "";
              navigate(
                organization
                  .map((org) =>
                    FormTemplates.edit(
                      org,
                      enrollmentPeriod.id,
                      formTemplateId,
                      FORM_TEMPLATE_TAB_HASH.CONTENT
                    )
                  )
                  .withDefault("#")
              );
            },
          });

          toast({
            title: "Form created",
            status: "success",
            isClosable: true,
          });
          onCancel();
        }
      } catch (error) {
        console.error(error);
        toast({
          title: "Error creating form",
          status: "error",
          isClosable: true,
        });
      }
    },
    [
      apolloClient.cache,
      createFormTemplateDraft,
      createFormTemplatePublished,
      enrollmentPeriod.id,
      flags,
      navigate,
      onCancel,
      organization,
      toast,
    ]
  );

  return (
    <Modal isOpen={isOpen} onClose={onCancel}>
      <ModalOverlay />
      <Formik<FormType>
        initialValues={initialValues}
        validateOnBlur={true}
        onSubmit={handleSubmitForm}
        validate={validateWithZod(FormSchema)}
      >
        {({
          values,
          handleReset,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => {
          const onCancelClick = () => {
            handleReset();
            onCancel();
          };

          return (
            <ModalContent as={Form} noValidate>
              <ModalHeader fontSize="lg" fontWeight="700">
                Create form
              </ModalHeader>

              <ModalBody>
                <Flex direction="column" gap={2}>
                  <InputWithValidationControl
                    id="name"
                    name="name"
                    label="Form name"
                    inputProps={{
                      placeholder: "E.g.: Registration 2024",
                      onChange: async (event) => {
                        const name = event.target.value;
                        await setFieldValue("name", name);
                        if (!values.userGeneratedKey) {
                          await setFieldValue("key", generateKey(name));
                        }
                      },
                    }}
                    isRequired
                  />

                  <InputWithValidationControl
                    id="key"
                    name="key"
                    label="Form key"
                    inputProps={{
                      placeholder: "E.g.: registration-2024-form",
                      onChange: async (event) => {
                        const key = event.target.value;
                        await setFieldValue("key", key);
                        await setFieldValue(
                          "userGeneratedKey",
                          key.trim().length > 0
                        );
                      },
                    }}
                    isRequired
                  />

                  <TextareaWithValidationControl
                    id="description"
                    name="description"
                    label="Form description"
                  />
                </Flex>
              </ModalBody>

              <ModalFooter gap={2}>
                <Button
                  variant="ghost"
                  colorScheme="gray"
                  onClick={onCancelClick}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  onClick={() => handleSubmit()}
                  isLoading={isSubmitting}
                >
                  Create form
                </Button>
              </ModalFooter>
            </ModalContent>
          );
        }}
      </Formik>
    </Modal>
  );
};
